import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import type { Movie } from "@/types/movie";

export function getMovieById(id: string): Movie | undefined {
  return movies.find((movie) => movie.id === id);
}

export function getCategoryName(key: string): string {
  return categories.find((category) => category.key === key)?.name ?? key;
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) return `${hours}小时${mins}分钟`;
  if (hours > 0) return `${hours}小时`;
  return `${mins}分钟`;
}

export function ratingTone(rating: number): "high" | "mid" | "low" {
  if (rating >= 8.5) return "high";
  if (rating >= 8) return "mid";
  return "low";
}

export interface MovieFilters {
  genre?: string;
  keyword?: string;
  yearRange?: string;
  ratingRange?: string;
  region?: string;
  sort?: string;
}

export function isMovieInGenre(movie: Movie, genre?: string): boolean {
  if (!genre) return true;
  return movie.genres.includes(genre);
}

export function filterAndSortMovies(list: Movie[], filters: MovieFilters): Movie[] {
  const keyword = (filters.keyword ?? "").trim().toLowerCase();
  let result = list.filter((movie) => {
    if (filters.genre && !movie.genres.includes(filters.genre)) return false;
    if (keyword) {
      const haystack = [movie.title, movie.originalTitle, movie.director, ...movie.cast, ...movie.tags]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(keyword)) return false;
    }
    if (filters.yearRange) {
      const [from, to] = filters.yearRange.split("-").map((value) => Number(value));
      if (movie.year < from || movie.year > to) return false;
    }
    if (filters.ratingRange) {
      const [from, to] = filters.ratingRange.split("-").map((value) => Number(value));
      if (movie.rating < from || (to > 0 && movie.rating > to)) return false;
    }
    if (filters.region && filters.region !== "all" && !movie.region.includes(filters.region)) return false;
    return true;
  });

  const sort = filters.sort ?? "heat";
  result = [...result].sort((a, b) => {
    if (sort === "new") return b.releaseDate.localeCompare(a.releaseDate);
    if (sort === "rating") return b.rating - a.rating;
    return b.heat - a.heat;
  });
  return result;
}

export function searchMovies(keyword: string, limit = 0): Movie[] {
  const trimmed = keyword.trim().toLowerCase();
  if (!trimmed) return [];
  const result = movies.filter((movie) =>
    [movie.title, movie.originalTitle, movie.director, ...movie.cast, ...movie.tags]
      .join(" ")
      .toLowerCase()
      .includes(trimmed),
  );
  return limit > 0 ? result.slice(0, limit) : result;
}

export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  const scored = movies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const shared = item.genres.filter((genre) => movie.genres.includes(genre)).length;
      return { item, shared };
    })
    .sort((a, b) => b.shared - a.shared || b.item.heat - a.item.heat)
    .map((entry) => entry.item);
  return scored.slice(0, limit);
}